import React, { useState, useEffect, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { Button, DropdownMenu, StatusBadge } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { Card, StatsCard } from '../common/Card';
import { Input } from '../common/Form';
import Pagination from '../common/Pagination';
import AssignEmailModal from './AssignEmailModal';
import DeleteGuestModal from './DeleteGuestModal';
import AddGuestModal from './AddGuestModal';
import GuestInfoModal from './GuestInfoModal';
import {
  getClubGuests,
  getDeletedClubGuests,
  getDeclinedClubGuests,
  restoreGuest,
  updateGuest,
  removeEmailFromGuest,
  updateGuestEloRatingsForClubSeason
} from '../../services/guestService';
import { getPlayerNameColor } from '../../utils/playerUtils';
import ConfirmationModal from '../common/ConfirmationModal';

const GuestManagement = ({ clubId, currentUser, seasonId }) => {
  const [guests, setGuests] = useState([]);
  const [deletedGuests, setDeletedGuests] = useState([]);
  const [declinedGuests, setDeclinedGuests] = useState([]);
  const [totalClubSessions, setTotalClubSessions] = useState(0);
  // Store total guest stats (not affected by search)
  const [totalGuestStats, setTotalGuestStats] = useState({
    totalGuests: 0,
    withEmail: 0,
    withoutEmail: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchTimeoutRef = useRef(null);
  const hasInitialLoadRef = useRef(false);
  
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [showAssignEmailModal, setShowAssignEmailModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeletedGuests, setShowDeletedGuests] = useState(false);
  const [showDeclinedGuests, setShowDeclinedGuests] = useState(false);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [showRemoveEmailModal, setShowRemoveEmailModal] = useState(false);
  const [guestToRemoveEmail, setGuestToRemoveEmail] = useState(null);
  const [showAddGuestModal, setShowAddGuestModal] = useState(false);
  const [guestForInfoModal, setGuestForInfoModal] = useState(null);
  const [editingEloGuest, setEditingEloGuest] = useState(null);
  const [editEloForm, setEditEloForm] = useState({
    mixedElo: '1200',
    levelsElo: '1200',
    singlesElo: '1000'
  });

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Inline editing state
  const [editingGuest, setEditingGuest] = useState(null);
  const [editForm, setEditForm] = useState({ firstName: '', lastName: '', gender: 'male' });
  
  // Email editing state
  const [editingEmailGuest, setEditingEmailGuest] = useState(null);
  const [editEmailForm, setEditEmailForm] = useState({ email: '' });
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0,
    totalPages: 0
  });

  // Refs to store current state values for stable callbacks
  // Initialize with default values, then sync with state
  const currentPageRef = useRef(1);
  const itemsPerPageRef = useRef(10);
  const sortFieldRef = useRef(null);
  const sortDirectionRef = useRef('asc');
  
  // Keep refs in sync with state
  useEffect(() => {
    currentPageRef.current = currentPage;
    itemsPerPageRef.current = itemsPerPage;
    sortFieldRef.current = sortField;
    sortDirectionRef.current = sortDirection;
  }, [currentPage, itemsPerPage, sortField, sortDirection]);

  // Fetch only the main guests list (for search and pagination)
  // This function is stable and accepts parameters to avoid recreation
  const fetchGuestsList = useCallback(async (page, limit, search, isSearch = false) => {
    if (isSearch) {
      setSearchLoading(true);
    } else {
    setLoading(true);
    }
    setError(null);
    
    try {
      // Use current sort values from refs (always up-to-date, no stale closure)
      const sort = sortFieldRef.current || 'created_at';
      const direction = sortDirectionRef.current || 'desc';
      const data = await getClubGuests(clubId, page, limit, sort, direction, search, seasonId);
      setGuests(data.guests);
      setTotalClubSessions(data.totalClubSessions);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Error fetching guests:', err);
      setError('Failed to load guests');
    } finally {
      if (isSearch) {
        setSearchLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [clubId, seasonId]); // seasonId affects ELO snapshot on each guest row

  // Fetch all guest-related data (only on initial load or refresh)
  const fetchAllGuestData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }
    setError(null);
    
    try {
      // Read current values from refs (always up-to-date, no stale closure)
      const page = currentPageRef.current;
      const limit = itemsPerPageRef.current;
      const sort = sortFieldRef.current || 'created_at';
      const direction = sortDirectionRef.current || 'desc';
      
      // Fetch main guests list for display
      const data = await getClubGuests(clubId, page, limit, sort, direction, '', seasonId);
      setGuests(data.guests);
      setTotalClubSessions(data.totalClubSessions);
      setPagination(data.pagination);
      
      // Fetch ALL guests (no pagination) to calculate total stats for KPI boxes
      // This ensures stats remain fixed regardless of search
      const allGuestsData = await getClubGuests(clubId, 1, 10000, sort, direction, '', seasonId);
      const allGuests = allGuestsData.guests || [];
      
      // Calculate and store total stats (not affected by search)
      const totalWithEmail = allGuests.filter(g => g.guest_invited_email).length;
      const totalWithoutEmail = allGuests.filter(g => !g.guest_invited_email).length;
      setTotalGuestStats({
        totalGuests: allGuests.length,
        withEmail: totalWithEmail,
        withoutEmail: totalWithoutEmail
      });
      
      // Also fetch deleted guests (no pagination needed for deleted guests)
      const deletedData = await getDeletedClubGuests(clubId);
      setDeletedGuests(deletedData);
      
      // Fetch declined guests (guests reported as incorrect)
      try {
        const declinedData = await getDeclinedClubGuests(clubId);
        setDeclinedGuests(declinedData || []);
      } catch (err) {
        console.error('Error loading declined guests:', err);
        // Don't fail the whole load if declined guests fail
      }
      
    } catch (err) {
      console.error('Error fetching guests:', err);
      setError('Failed to load guests');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [clubId, seasonId, currentPage, itemsPerPage, sortField, sortDirection]);

  // Load (or reload) guest data when club or current season changes
  useEffect(() => {
    if (!clubId) return;
    if (!hasInitialLoadRef.current) {
      hasInitialLoadRef.current = true;
    }
    fetchAllGuestData();
  }, [clubId, seasonId, fetchAllGuestData]);

  // Debounce search term - update debouncedSearchTerm after user stops typing
  useEffect(() => {
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to first page when starting a search (not when clearing)
      if (searchTerm.trim() !== '') {
        setCurrentPage(1);
      }
    }, 400); // 400ms delay

    // Cleanup on unmount or when searchTerm changes
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm]); // Only depend on searchTerm - this prevents the feedback loop

  // Track previous debouncedSearchTerm to detect when search is cleared vs when it's just empty initially
  const prevDebouncedSearchRef = useRef(debouncedSearchTerm);
  
  // Fetch guests when debouncedSearchTerm changes (search trigger - only affects table)
  useEffect(() => {
    if (!clubId || !hasInitialLoadRef.current) return; // Don't run until initial load is done
    
    const wasSearching = prevDebouncedSearchRef.current.trim() !== '';
    const isSearching = debouncedSearchTerm.trim() !== '';
    
    // If there's a search term, fetch with search (only main list, not all data)
    if (isSearching) {
      fetchGuestsList(1, itemsPerPage, debouncedSearchTerm.trim(), true);
    } else if (wasSearching && !isSearching) {
      // Search was just cleared (went from having search to no search)
      // Just fetch the main list without search - only the table updates
      fetchGuestsList(currentPage, itemsPerPage, '', false);
    }
    // If it was empty and still empty, do nothing (initial state or no change)
    
    prevDebouncedSearchRef.current = debouncedSearchTerm;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]); // Only trigger when debouncedSearchTerm changes

  // When pagination or sorting changes (but NOT when searching), refetch main list only
  useEffect(() => {
    // Only run if we're NOT searching and initial load is done
    // Skip if debouncedSearchTerm just changed (let the search effect handle it)
    if (clubId && hasInitialLoadRef.current && !debouncedSearchTerm.trim()) {
      fetchGuestsList(currentPage, itemsPerPage, '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId, seasonId, currentPage, itemsPerPage, sortField, sortDirection]); // Removed debouncedSearchTerm and fetchGuestsList to prevent conflicts

  const handleAssignEmail = (guest) => {
    setSelectedGuest(guest);
    setShowAssignEmailModal(true);
  };

  const handleDeleteClick = (guest) => {
    setSelectedGuest(guest);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = (deletedGuest) => {
    // Remove the deleted guest from the local state
    setGuests(prevGuests => prevGuests.filter(guest => guest.id !== deletedGuest.id));
    setShowDeleteModal(false);
    setSelectedGuest(null);
  };

  const handleRestoreGuest = async (guest) => {
    try {
      await restoreGuest(guest.id, clubId);
      // Remove from deleted guests and add back to active guests
      setDeletedGuests(prev => prev.filter(g => g.id !== guest.id));
      setGuests(prev => [...prev, guest]);
    } catch (err) {
      console.error('Error restoring guest:', err);
      setError('Failed to restore guest');
    }
  };

  const handleEmailAssigned = async (updatedGuest) => {
    // Refresh all guest data (including declined guests, etc.)
    // This will also update the total stats
    await fetchAllGuestData();
    
    // Refresh declined guests list
    // If email was assigned to a declined guest, it should appear in the list
    // If email was assigned to a different guest (correcting the assignment), the declined guest should disappear
    try {
      const declinedData = await getDeclinedClubGuests(clubId);
      setDeclinedGuests(declinedData || []);
    } catch (err) {
      console.error('Error refreshing declined guests:', err);
    }
  };

  const handlePageChange = (page, newItemsPerPage) => {
    setCurrentPage(page);
    if (newItemsPerPage !== itemsPerPage) {
      setItemsPerPage(newItemsPerPage);
    }
    // fetchGuestsList will be called by useEffect when currentPage/itemsPerPage changes
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Page reset is handled by the debounce useEffect
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    // Reset to first page when sorting changes
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return '↕️'; // Neutral sort icon
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Inline editing functions
  const startEditing = (guest) => {
    setEditingEloGuest(null);
    setEditingGuest(guest.id);
    setEditForm({
      firstName: guest.first_name || '',
      lastName: guest.last_name || '',
      gender: guest.gender || 'male'
    });
  };

  const cancelEditing = () => {
    setEditingGuest(null);
    setEditForm({ firstName: '', lastName: '', gender: 'male' });
  };

  const saveEditing = async () => {
    if (!editingGuest) return;

    try {
      await updateGuest(editingGuest, {
        first_name: editForm.firstName.trim(),
        last_name: editForm.lastName.trim(),
        gender: editForm.gender
      });

      // Update local state
      setGuests(prevGuests =>
        prevGuests.map(guest =>
          guest.id === editingGuest
            ? { ...guest, first_name: editForm.firstName.trim(), last_name: editForm.lastName.trim(), gender: editForm.gender }
            : guest
        )
      );

      setEditingGuest(null);
      setEditForm({ firstName: '', lastName: '', gender: 'male' });
    } catch (error) {
      console.error('Error updating guest:', error);
      setError('Failed to update guest');
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  // Email editing functions
  const startEditingEmail = (guest) => {
    setEditingEloGuest(null);
    setEditingEmailGuest(guest.id);
    setEditEmailForm({
      email: guest.guest_invited_email || ''
    });
  };

  const cancelEditingEmail = () => {
    setEditingEmailGuest(null);
    setEditEmailForm({ email: '' });
  };

  const startEditingElo = (guest) => {
    if (!seasonId || !guest.canEditEloRatings) return;
    setEditingGuest(null);
    setEditingEmailGuest(null);
    setEditingEloGuest(guest.id);
    const e = guest.eloForCurrentSeason;
    setEditEloForm({
      mixedElo: String(e?.mixed_elo_rating ?? 1200),
      levelsElo: String(e?.levels_elo_rating ?? 1200),
      singlesElo: String(e?.singles_elo_rating ?? 1000)
    });
  };

  const cancelEditingElo = () => {
    setEditingEloGuest(null);
    setEditEloForm({ mixedElo: '1200', levelsElo: '1200', singlesElo: '1000' });
  };

  const saveEditingElo = async () => {
    if (!editingEloGuest || !seasonId || !clubId) return;

    try {
      const updated = await updateGuestEloRatingsForClubSeason(
        editingEloGuest,
        clubId,
        seasonId,
        {
          mixed_elo_rating: editEloForm.mixedElo,
          levels_elo_rating: editEloForm.levelsElo,
          singles_elo_rating: editEloForm.singlesElo
        }
      );

      setGuests((prev) =>
        prev.map((g) =>
          g.id === editingEloGuest
            ? {
                ...g,
                eloForCurrentSeason: {
                  id: updated.id,
                  mixed_elo_rating: updated.mixed_elo_rating,
                  levels_elo_rating: updated.levels_elo_rating,
                  singles_elo_rating: updated.singles_elo_rating
                }
              }
            : g
        )
      );

      setError(null);
      cancelEditingElo();
    } catch (err) {
      console.error('Error updating guest ELO:', err);
      setError(err.message || 'Failed to update ELO');
    }
  };

  const handleEditEloFormChange = (field, value) => {
    setEditEloForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveEmailEditing = async () => {
    if (!editingEmailGuest) return;

    try {
      const trimmedEmail = editEmailForm.email.trim();
      
      // If email is empty, remove it
      if (!trimmedEmail) {
        const guest = guests.find(g => g.id === editingEmailGuest);
        if (guest && (guest.merged_at || guest.merged_into_user_id)) {
          setError('Cannot remove email from a merged guest account');
          return;
        }
        
        await removeEmailFromGuest(editingEmailGuest);
        
        // Update local state
        setGuests(prevGuests =>
          prevGuests.map(g =>
            g.id === editingEmailGuest
              ? {
                  ...g,
                  guest_invited_email: null,
                  guest_email_assigned_at: null,
                  guest_email_assigned_by: null
                }
              : g
          )
        );
      } else {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
          setError('Please enter a valid email address');
          return;
        }

        const guest = guests.find(g => g.id === editingEmailGuest);
        const hadEmail = !!guest?.guest_invited_email;

        await updateGuest(editingEmailGuest, {
          guest_invited_email: trimmedEmail.toLowerCase(),
          guest_email_assigned_at: new Date().toISOString(),
          guest_email_assigned_by: currentUser.id
        });

        // Update local state
        setGuests(prevGuests =>
          prevGuests.map(guest =>
            guest.id === editingEmailGuest
              ? { 
                  ...guest, 
                  guest_invited_email: trimmedEmail.toLowerCase(),
                  guest_email_assigned_at: new Date().toISOString(),
                  guest_email_assigned_by: currentUser.id
                }
              : guest
          )
        );
        
        // Update stats - if guest didn't have email before, increment withEmail and decrement withoutEmail
        if (!hadEmail) {
          setTotalGuestStats(prev => ({
            ...prev,
            withEmail: prev.withEmail + 1,
            withoutEmail: Math.max(0, prev.withoutEmail - 1)
          }));
        }
      }

      setEditingEmailGuest(null);
      setEditEmailForm({ email: '' });
      setError(null);
    } catch (error) {
      console.error('Error updating guest email:', error);
      setError(error.message || 'Failed to update guest email');
    }
  };

  const handleEmailFormChange = (value) => {
    setEditEmailForm(prev => ({ ...prev, email: value }));
  };

  const handleRemoveEmailClick = (guest) => {
    if (!guest || !guest.guest_invited_email) return;
    
    // Check if guest is merged (shouldn't happen due to filtering, but double-check)
    if (guest.merged_at || guest.merged_into_user_id) {
      setError('Cannot remove email from a merged guest account');
      return;
    }

    setGuestToRemoveEmail(guest);
    setShowRemoveEmailModal(true);
  };

  const handleConfirmRemoveEmail = async () => {
    if (!guestToRemoveEmail) return;

    try {
      setRemovingEmail(guestToRemoveEmail.id);
      setError(null);
      setShowRemoveEmailModal(false);
      
      await removeEmailFromGuest(guestToRemoveEmail.id);
      
      // Refresh declined guests list from database first
      // Removing the email resolves the "incorrect assignment" issue
      // The query only returns declined guests that still have emails assigned
      // So this guest will no longer appear after email removal
      try {
        const declinedData = await getDeclinedClubGuests(clubId);
        setDeclinedGuests(declinedData || []);
      } catch (err) {
        console.error('Error refreshing declined guests:', err);
        // Fallback: remove from local state
        setDeclinedGuests(prevDeclinedGuests =>
          prevDeclinedGuests.filter(g => g.id !== guestToRemoveEmail.id)
        );
      }
      
      // Refresh all guest data to update stats
      // The guest should now appear in the main table (declined guests without emails are included)
      await fetchAllGuestData();

      setGuestToRemoveEmail(null);
    } catch (err) {
      console.error('Error removing email:', err);
      setError(err.message || 'Failed to remove email');
    } finally {
      setRemovingEmail(null);
    }
  };

  const handleCancelRemoveEmail = () => {
    setShowRemoveEmailModal(false);
    setGuestToRemoveEmail(null);
  };

  // No need for client-side filtering - search is now done server-side
  const filteredGuests = guests;

  const guestDisplayName = (guest) =>
    `${guest.first_name || ''} ${guest.last_name || ''}`.trim() || 'guest';

  const getGuestOverflowItems = (guest) => {
    const items = [
      {
        label: 'Activity',
        title: 'Last session, recent games, and totals',
        onClick: () => setGuestForInfoModal(guest),
      },
    ];

    if (guest.guest_invited_email) {
      items.push({
        label: 'Edit Email',
        onClick: () => startEditingEmail(guest),
      });
      if (!guest.merged_at && !guest.merged_into_user_id) {
        const removing = removingEmail === guest.id;
        items.push({
          label: removing ? 'Removing...' : 'Remove Email',
          title: 'Remove email assignment',
          disabled: removing,
          onClick: () => handleRemoveEmailClick(guest),
        });
      }
    }

    items.push({
      label: 'Delete',
      variant: 'danger',
      onClick: () => handleDeleteClick(guest),
    });

    return items;
  };

  const renderGuestRowActions = (guest) => {
    const name = guestDisplayName(guest);
    return (
      <div className="flex flex-wrap items-center gap-2">
        {!guest.guest_invited_email ? (
          <Button onClick={() => handleAssignEmail(guest)} variant="primary" size="small">
            Assign Email
          </Button>
        ) : null}
        <DropdownMenu
          usePortal
          useMobileSheet
          align="right"
          sheetTitle={`Actions for ${name}`}
          trigger={
            <Button type="button" variant="outline" size="small" aria-label={`Actions for ${name}`}>
              Actions
            </Button>
          }
          items={getGuestOverflowItems(guest)}
        />
      </div>
    );
  };

  /** Stacked layout for small screens — same behaviours as desktop table rows */
  const renderGuestMobileCard = (guest) => (
    <div
      key={guest.id}
      className="rounded-lg border border-border-medium bg-surface-subtle p-3"
    >
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-info-light">
          <span className="text-sm font-medium text-info-dark">
            {guest.first_name?.[0]}
            {guest.last_name?.[0]}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          {editingGuest === guest.id ? (
            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(e) => handleEditFormChange('firstName', e.target.value)}
                  className="min-w-0 flex-1 rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="First name"
                  autoFocus
                />
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(e) => handleEditFormChange('lastName', e.target.value)}
                  className="min-w-0 flex-1 rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Last name"
                />
              </div>
              <select
                value={editForm.gender}
                onChange={(e) => handleEditFormChange('gender', e.target.value)}
                className="w-full rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={saveEditing}
                  className="rounded bg-success px-2 py-1 text-xs text-text-inverse hover:opacity-90"
                >
                  ✓ Save
                </button>
                <button
                  type="button"
                  onClick={cancelEditing}
                  className="rounded bg-surface-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-elevated"
                >
                  ✕ Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <span className={`text-sm font-medium ${getPlayerNameColor(guest.gender)}`}>
                {guest.first_name} {guest.last_name}
              </span>
              <span className="inline-flex items-center rounded bg-info-light px-1.5 py-0.5 text-xs font-medium text-info-dark">
                👤 Guest
              </span>
              <button
                type="button"
                onClick={() => setGuestForInfoModal(guest)}
                className="text-xs text-text-muted hover:text-primary"
                title="View sessions and games (identify guest)"
                aria-label="View guest activity and recent games"
              >
                ℹ️
              </button>
              {guest.canEditEloRatings && (
                <button
                  type="button"
                  onClick={() => startEditingElo(guest)}
                  className="text-xs font-medium text-primary hover:underline"
                  title="Edit starting ELO (only before first game at this club)"
                >
                  ELO
                </button>
              )}
              <button
                type="button"
                onClick={() => startEditing(guest)}
                className="text-xs text-text-muted hover:text-primary"
                title="Edit name"
              >
                ✏️
              </button>
            </div>
          )}
          {editingEloGuest === guest.id && (
            <div className="mt-2 w-full rounded-lg border border-border-medium bg-surface-subtle p-3">
              <Text className="mb-2 block text-xs text-text-muted">
                Starting ELO for the current season (locked after their first game here)
              </Text>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="mb-0.5 block text-xs text-text-muted">Mixed</label>
                  <input
                    type="number"
                    min={100}
                    max={4000}
                    value={editEloForm.mixedElo}
                    onChange={(e) => handleEditEloFormChange('mixedElo', e.target.value)}
                    className="w-full rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-xs text-text-muted">Levels</label>
                  <input
                    type="number"
                    min={100}
                    max={4000}
                    value={editEloForm.levelsElo}
                    onChange={(e) => handleEditEloFormChange('levelsElo', e.target.value)}
                    className="w-full rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="mb-0.5 block text-xs text-text-muted">Singles</label>
                  <input
                    type="number"
                    min={100}
                    max={4000}
                    value={editEloForm.singlesElo}
                    onChange={(e) => handleEditEloFormChange('singlesElo', e.target.value)}
                    className="w-full rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={saveEditingElo}
                  className="rounded bg-success px-2 py-1 text-xs text-text-inverse hover:opacity-90"
                >
                  ✓ Save ELO
                </button>
                <button
                  type="button"
                  onClick={cancelEditingElo}
                  className="rounded bg-surface-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-elevated"
                >
                  ✕ Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
        <span>
          <span className="text-text-muted">Sessions </span>
          <span
            className={`rounded-full px-1.5 py-0.5 font-medium ${
              (guest.totalSessions || 0) > 0 ? 'bg-info-light text-info-dark' : 'text-text-muted'
            }`}
          >
            {guest.totalSessions || 0}
          </span>
        </span>
        {editingEmailGuest !== guest.id && (
          <span className="inline-flex min-w-0 items-center gap-1">
            {guest.guest_invited_email ? (
              <>
                <StatusBadge variant="success" size="small">
                  ✅ Email
                </StatusBadge>
                <span className="max-w-[9rem] truncate text-text-secondary sm:max-w-xs">
                  {guest.guest_invited_email}
                </span>
              </>
            ) : (
              <StatusBadge variant="warning" size="small">
                ⏳ No Email
              </StatusBadge>
            )}
          </span>
        )}
        <span className="text-text-muted">
          Created{' '}
          {guest.created_at && !isNaN(new Date(guest.created_at).getTime())
            ? format(new Date(guest.created_at), 'MMM dd, yyyy')
            : 'N/A'}
        </span>
      </div>

      {editingEmailGuest === guest.id && (
        <div className="mt-2 space-y-2">
          <input
            type="email"
            value={editEmailForm.email}
            onChange={(e) => handleEmailFormChange(e.target.value)}
            className="w-full rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Enter email address"
            autoFocus
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={saveEmailEditing}
              className="rounded bg-success px-2 py-1 text-xs text-text-inverse hover:opacity-90"
            >
              ✓ Save
            </button>
            <button
              type="button"
              onClick={cancelEditingEmail}
              className="rounded bg-surface-subtle px-2 py-1 text-xs text-text-secondary hover:bg-surface-elevated"
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 border-t border-border-medium pt-3">
        {renderGuestRowActions(guest)}
      </div>
    </div>
  );

  const addGuestModal = (
    <AddGuestModal
      isOpen={showAddGuestModal}
      onClose={() => setShowAddGuestModal(false)}
      onGuestAdded={async () => {
        await fetchAllGuestData({ silent: true });
      }}
      sessionId={null}
      clubId={clubId}
      currentUser={currentUser}
      seasonId={seasonId}
    />
  );

  return (
    <>
      {addGuestModal}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Typography variant="body" className="text-text-secondary">Loading guests...</Typography>
        </div>
      ) : (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Heading level={2}>Guest Management</Heading>
          <Text className="text-text-secondary">
            Manage guest players and assign emails for account linking
          </Text>
        </div>
        <Button
          variant="primary"
          size="small"
          className="shrink-0 self-start sm:self-auto"
          onClick={() => setShowAddGuestModal(true)}
        >
          Add New Guest
        </Button>
      </div>

      {/* Declined/Reported Incorrect Guest Accounts */}
      {declinedGuests.length > 0 && (
        <Card variant="default" size="medium" className="border-l-4 border-l-warning">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <Heading level={3} className="mb-2">Reported Incorrect Guest Accounts</Heading>
              <Text className="text-sm text-text-secondary">
                {declinedGuests.length} guest account{declinedGuests.length !== 1 ? 's' : ''} reported as incorrect by users. Review and correct email assignments.
              </Text>
            </div>
            <Button
              variant="outline"
              size="small"
              className="shrink-0 self-start"
              onClick={() => setShowDeclinedGuests(!showDeclinedGuests)}
            >
              {showDeclinedGuests ? 'Hide' : 'Show'} ({declinedGuests.length})
            </Button>
          </div>

          {showDeclinedGuests && (
            <div className="space-y-4">
              {declinedGuests.map((guest) => {
                const declinedByUser = guest.declinedByUser;
                const userMessage = guest.userMessage;
                const declinedAt = guest.declinedAt;

                return (
                  <Card key={guest.id} variant="muted" size="small" className="border-l-4 border-l-warning">
                    <div className="space-y-3">
                      {/* Guest Info */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Typography variant="strong" className="text-base">
                              {guest.first_name} {guest.last_name || ''}
                            </Typography>
                            <StatusBadge variant="warning" size="small">Reported Incorrect</StatusBadge>
                          </div>
                          <div className="space-y-1 text-sm">
                            {guest.guest_invited_email && (
                              <div>
                                <Text className="text-text-secondary">Assigned Email:</Text>
                                <Typography variant="strong" className="ml-2">{guest.guest_invited_email}</Typography>
                              </div>
                            )}
                            <div>
                              <Text className="text-text-secondary">Guest Created:</Text>
                              <Typography variant="strong" className="ml-2">
                                {guest.created_at && !isNaN(new Date(guest.created_at).getTime())
                                  ? format(new Date(guest.created_at), 'MMM dd, yyyy')
                                  : 'N/A'}
                              </Typography>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          {guest.guest_invited_email && (
                            <Button
                              variant="outline"
                              size="small"
                              onClick={() => handleRemoveEmailClick(guest)}
                              disabled={removingEmail === guest.id}
                              className="text-danger-dark border-danger hover:bg-danger-light"
                            >
                              {removingEmail === guest.id ? 'Removing...' : 'Remove Email'}
                            </Button>
                          )}
                          <Button
                            variant="primary"
                            size="small"
                            onClick={() => handleAssignEmail(guest)}
                          >
                            {guest.guest_invited_email ? 'Correct Email' : 'Assign Email'}
                          </Button>
                        </div>
                      </div>

                      {/* Declined By Info */}
                      {declinedByUser && (
                        <div className="bg-warning-light border border-warning rounded-lg p-3">
                          <Typography variant="strong" className="text-sm mb-1">Reported By:</Typography>
                          <Text className="text-sm text-text-primary">
                            {declinedByUser.first_name} {declinedByUser.last_name || ''} ({declinedByUser.email})
                          </Text>
                          {declinedAt && !isNaN(new Date(declinedAt).getTime()) && (
                            <Text className="text-xs text-text-muted mt-1">
                              Reported on: {format(new Date(declinedAt), 'MMM dd, yyyy h:mm a')}
                            </Text>
                          )}
                        </div>
                      )}

                      {/* User Message */}
                      {userMessage && (
                        <div className="bg-info-light border border-info rounded-lg p-3">
                          <Typography variant="strong" className="text-sm mb-1">User Message:</Typography>
                          <Text className="text-sm text-info-dark">{userMessage}</Text>
                        </div>
                      )}

                      {/* Action */}
                      <div className="flex items-center gap-2 pt-2 border-t border-border-medium">
                        <Text className="text-xs text-text-muted">
                          Click "Correct Email" to assign the correct email address to this guest account.
                        </Text>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Stats — compact on mobile (2×2), full row on lg */}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <StatsCard
          dense
          title="Guests"
          value={totalGuestStats.totalGuests.toString()}
          icon="👥"
          iconBg="bg-surface-subtle"
        />
        <StatsCard
          dense
          title="W/ email"
          value={totalGuestStats.withEmail.toString()}
          icon="✅"
          iconBg="bg-success-light"
        />
        <StatsCard
          dense
          title="No email"
          value={totalGuestStats.withoutEmail.toString()}
          icon="⏳"
          iconBg="bg-warning-light"
        />
        <StatsCard
          dense
          title="Sessions"
          value={totalClubSessions.toString()}
          icon="📅"
          iconBg="bg-info-light"
        />
      </div>

      {/* Error Message */}
      {error && (
        <Card variant="danger" size="small">
          <Typography variant="body" className="text-text-primary">{error}</Typography>
        </Card>
      )}

      {/* Search */}
      <Card variant="default" size="small">
        <div className="relative">
          <Input
            type="search"
            placeholder="Search guests by name or email..."
            value={searchTerm}
            onChange={handleSearch}
            disabled={loading}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="search"
          />
          {searchLoading && (
            <div
              className="pointer-events-none absolute inset-y-0 right-3 flex items-center"
              aria-hidden
            >
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          )}
        </div>
      </Card>

      {/* No Guests */}
      {guests.length === 0 && !loading && (
        <Card variant="default" size="medium" className="text-center py-12">
          <div className="text-6xl mb-4">👤</div>
          <Heading level={3} className="mb-2">No Guests Found</Heading>
          <Text className="text-text-secondary">
            Guest players will appear here once they're added to sessions.
          </Text>
        </Card>
      )}

      {/* Guests Table */}
      {filteredGuests.length > 0 && (
        <Card variant="default" size="medium" className="overflow-hidden">
          <div className="space-y-3 p-3 sm:p-4 lg:hidden">
            {filteredGuests.map((guest) => renderGuestMobileCard(guest))}
          </div>
          <div className="hidden overflow-x-auto lg:block">
            <table className="min-w-full divide-y divide-border-medium">
              <thead className="bg-surface-subtle">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider cursor-pointer hover:bg-surface-elevated"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Guest</span>
                      <span className="text-xs">{getSortIcon('name')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider cursor-pointer hover:bg-surface-elevated"
                    onClick={() => handleSort('totalSessions')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Total Sessions</span>
                      <span className="text-xs">{getSortIcon('totalSessions')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider cursor-pointer hover:bg-surface-elevated"
                    onClick={() => handleSort('emailStatus')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Email Status</span>
                      <span className="text-xs">{getSortIcon('emailStatus')}</span>
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider cursor-pointer hover:bg-surface-elevated"
                    onClick={() => handleSort('created')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      <span className="text-xs">{getSortIcon('created')}</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-border-medium">
                {filteredGuests.map((guest) => (
                  <tr key={guest.id} className="hover:bg-surface-subtle">
                    <td className="px-6 py-4 min-w-[220px] max-w-md align-top whitespace-normal">
                      <div className="flex items-start">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-info-light flex items-center justify-center">
                            <span className="text-sm font-medium text-info-dark">
                              {guest.first_name?.[0]}{guest.last_name?.[0]}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          {editingGuest === guest.id ? (
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-center gap-2">
                                <input
                                  type="text"
                                  value={editForm.firstName}
                                  onChange={(e) => handleEditFormChange('firstName', e.target.value)}
                                  className="px-2 py-1 text-sm bg-surface border border-border-medium rounded focus:outline-none focus:ring-2 focus:ring-primary text-text-primary placeholder:text-text-muted"
                                  placeholder="First name"
                                  autoFocus
                                />
                                <input
                                  type="text"
                                  value={editForm.lastName}
                                  onChange={(e) => handleEditFormChange('lastName', e.target.value)}
                                  className="px-2 py-1 text-sm bg-surface border border-border-medium rounded focus:outline-none focus:ring-2 focus:ring-primary text-text-primary placeholder:text-text-muted"
                                  placeholder="Last name"
                                />
                              </div>
                              <div className="flex items-center space-x-2">
                                <select
                                  value={editForm.gender}
                                  onChange={(e) => handleEditFormChange('gender', e.target.value)}
                                  className="px-2 py-1 text-sm bg-surface border border-border-medium rounded focus:outline-none focus:ring-2 focus:ring-primary text-text-primary"
                                >
                                  <option value="male">Male</option>
                                  <option value="female">Female</option>
                                </select>
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  type="button"
                                  onClick={saveEditing}
                                  className="px-2 py-1 text-xs bg-success text-text-inverse rounded hover:opacity-90"
                                >
                                  ✓ Save
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditing}
                                  className="px-2 py-1 text-xs bg-surface-subtle text-text-secondary rounded hover:bg-surface-elevated"
                                >
                                  ✕ Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="text-sm font-medium flex flex-wrap items-center gap-x-2 gap-y-1">
                              <span className={getPlayerNameColor(guest.gender)}>
                                {guest.first_name} {guest.last_name}
                              </span>
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-info-light text-info-dark">
                                👤 Guest
                              </span>
                              <button
                                type="button"
                                onClick={() => setGuestForInfoModal(guest)}
                                className="text-text-muted hover:text-primary text-xs"
                                title="View sessions and games (identify guest)"
                                aria-label="View guest activity and recent games"
                              >
                                ℹ️
                              </button>
                              {guest.canEditEloRatings && (
                                <button
                                  type="button"
                                  onClick={() => startEditingElo(guest)}
                                  className="text-xs font-medium text-primary hover:underline"
                                  title="Edit starting ELO (only before first game at this club)"
                                >
                                  ELO
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => startEditing(guest)}
                                className="text-text-muted hover:text-primary text-xs"
                                title="Edit name"
                              >
                                ✏️
                              </button>
                            </div>
                          )}
                          {editingEloGuest === guest.id && (
                            <div className="mt-2 rounded-lg border border-border-medium bg-surface-subtle p-3">
                              <Text className="mb-2 block text-xs text-text-muted">
                                Starting ELO for the current season (locked after their first game here)
                              </Text>
                              <div className="grid grid-cols-3 gap-2">
                                <div>
                                  <label className="mb-0.5 block text-xs text-text-muted">Mixed</label>
                                  <input
                                    type="number"
                                    min={100}
                                    max={4000}
                                    value={editEloForm.mixedElo}
                                    onChange={(e) => handleEditEloFormChange('mixedElo', e.target.value)}
                                    className="w-full rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                  />
                                </div>
                                <div>
                                  <label className="mb-0.5 block text-xs text-text-muted">Levels</label>
                                  <input
                                    type="number"
                                    min={100}
                                    max={4000}
                                    value={editEloForm.levelsElo}
                                    onChange={(e) => handleEditEloFormChange('levelsElo', e.target.value)}
                                    className="w-full rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                  />
                                </div>
                                <div>
                                  <label className="mb-0.5 block text-xs text-text-muted">Singles</label>
                                  <input
                                    type="number"
                                    min={100}
                                    max={4000}
                                    value={editEloForm.singlesElo}
                                    onChange={(e) => handleEditEloFormChange('singlesElo', e.target.value)}
                                    className="w-full rounded border border-border-medium bg-surface px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                  />
                                </div>
                              </div>
                              <div className="mt-2 flex flex-wrap gap-2">
                                <button
                                  type="button"
                                  onClick={saveEditingElo}
                                  className="px-2 py-1 text-xs bg-success text-text-inverse rounded hover:opacity-90"
                                >
                                  ✓ Save ELO
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditingElo}
                                  className="px-2 py-1 text-xs bg-surface-subtle text-text-secondary rounded hover:bg-surface-elevated"
                                >
                                  ✕ Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium px-2 py-1 rounded-full ${
                          (guest.totalSessions || 0) > 0 
                            ? 'bg-info-light text-info-dark' 
                            : 'bg-surface-subtle text-text-muted'
                        }`}>
                          {guest.totalSessions || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingEmailGuest === guest.id ? (
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <input
                              type="email"
                              value={editEmailForm.email}
                              onChange={(e) => handleEmailFormChange(e.target.value)}
                              className="px-2 py-1 text-sm bg-surface border border-border-medium rounded focus:outline-none focus:ring-2 focus:ring-primary text-text-primary placeholder:text-text-muted"
                              placeholder="Enter email address"
                              autoFocus
                            />
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={saveEmailEditing}
                              className="px-2 py-1 text-xs bg-success text-text-inverse rounded hover:opacity-90"
                            >
                              ✓ Save
                            </button>
                            <button
                              onClick={cancelEditingEmail}
                              className="px-2 py-1 text-xs bg-surface-subtle text-text-secondary rounded hover:bg-surface-elevated"
                            >
                              ✕ Cancel
                            </button>
                          </div>
                        </div>
                      ) : guest.guest_invited_email ? (
                        <div>
                          <div className="flex items-center space-x-2">
                            <StatusBadge variant="success" size="small">
                              ✅ Email Assigned
                            </StatusBadge>
                            <button
                              onClick={() => startEditingEmail(guest)}
                              className="text-text-muted hover:text-primary text-xs"
                              title="Edit email"
                            >
                              ✏️
                            </button>
                          </div>
                          <Typography variant="body" className="text-text-secondary mt-1">
                            {guest.guest_invited_email}
                          </Typography>
                          {guest.guest_email_assigned_at && (
                            <Typography variant="subtle" className="text-xs mt-1">
                              Assigned {format(new Date(guest.guest_email_assigned_at), 'MMM dd, yyyy')}
                            </Typography>
                          )}
                        </div>
                      ) : (
                        <StatusBadge variant="warning" size="small">
                          ⏳ No Email
                        </StatusBadge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Typography variant="body" className="text-text-muted">
                        {guest.created_at && !isNaN(new Date(guest.created_at).getTime())
                          ? format(new Date(guest.created_at), 'MMM dd, yyyy')
                          : 'N/A'}
                      </Typography>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {renderGuestRowActions(guest)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-border-medium">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={handlePageChange}
              showItemsPerPage={true}
              itemsPerPageOptions={[5, 10, 25, 50]}
            />
          </div>
        </Card>
      )}

      {/* Deleted Guests Section */}
      {deletedGuests.length > 0 && (
        <Card variant="default" size="medium" className="mt-6 overflow-hidden">
          <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <Heading variant="h3" className="text-text-primary">
                Deleted Guests ({deletedGuests.length})
              </Heading>
              <Text className="mt-1 text-text-secondary">
                Guests marked for deletion. They will be permanently removed after 30 days.
              </Text>
            </div>
            <Button
              onClick={() => setShowDeletedGuests(!showDeletedGuests)}
              variant="secondary"
              size="small"
              className="shrink-0 self-start"
            >
              {showDeletedGuests ? 'Hide' : 'Show'} Deleted
            </Button>
          </div>

          {showDeletedGuests && (
            <>
              <div className="space-y-3 p-3 sm:p-4 lg:hidden">
                {deletedGuests.map((guest) => (
                  <div
                    key={guest.id}
                    className="rounded-lg border border-border-medium bg-surface-subtle p-3"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-subtle">
                        <span className="text-sm font-medium text-text-primary">
                          {guest.first_name?.[0]}
                          {guest.last_name?.[0]}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`text-sm font-medium ${getPlayerNameColor(guest.gender)}`}>
                            {guest.first_name} {guest.last_name}
                          </span>
                          <span className="inline-flex items-center rounded bg-danger-light px-1.5 py-0.5 text-xs font-medium text-danger-dark">
                            🗑️ Deleted
                          </span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-3 text-xs text-text-secondary">
                          <span>Sessions: {guest.totalSessions || 0}</span>
                          <span>
                            Deleted {format(new Date(guest.deletion_requested_at), 'MMM dd, yyyy')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <Button onClick={() => handleRestoreGuest(guest)} variant="primary" size="small">
                        Restore
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
              <div className="hidden overflow-x-auto lg:block">
                <table className="min-w-full divide-y divide-border-medium">
                  <thead className="bg-surface-subtle">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-muted">
                        Guest
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-muted">
                        Total Sessions
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-muted">
                        Deleted
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-text-muted">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-medium bg-surface">
                    {deletedGuests.map((guest) => (
                      <tr key={guest.id} className="hover:bg-surface-subtle">
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-surface-subtle">
                              <span className="text-sm font-medium text-text-primary">
                                {guest.first_name?.[0]}
                                {guest.last_name?.[0]}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="flex items-center space-x-2 text-sm font-medium">
                                <span className={getPlayerNameColor(guest.gender)}>
                                  {guest.first_name} {guest.last_name}
                                </span>
                                <span className="inline-flex items-center rounded bg-danger-light px-1.5 py-0.5 text-xs font-medium text-danger-dark">
                                  🗑️ Deleted
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <div className="flex items-center">
                            <span className="inline-flex items-center rounded-full bg-info-light px-2 py-1 text-xs font-medium text-info-dark">
                              {guest.totalSessions || 0}
                            </span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <Text className="text-sm text-text-muted">
                            {format(new Date(guest.deletion_requested_at), 'MMM dd, yyyy')}
                          </Text>
                        </td>
                        <td className="whitespace-nowrap px-6 py-4">
                          <Button onClick={() => handleRestoreGuest(guest)} variant="primary" size="small">
                            Restore
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      )}

      {/* No Search Results */}
      {filteredGuests.length === 0 && guests.length > 0 && searchTerm && (
        <Card variant="default" size="medium" className="text-center py-8">
          <div className="text-4xl mb-4">🔍</div>
          <Heading level={3} className="mb-2">No Guests Found</Heading>
          <Text className="text-text-secondary">
            No guests match your search criteria. Try adjusting your search.
          </Text>
        </Card>
      )}

      <GuestInfoModal
        isOpen={Boolean(guestForInfoModal)}
        onClose={() => setGuestForInfoModal(null)}
        guest={guestForInfoModal}
        clubId={clubId}
      />

      {/* Assign Email Modal */}
      <AssignEmailModal
        isOpen={showAssignEmailModal}
        onClose={() => {
          setShowAssignEmailModal(false);
          setSelectedGuest(null);
        }}
        onEmailAssigned={handleEmailAssigned}
        guest={selectedGuest}
        currentUser={currentUser}
        clubId={clubId}
      />

      {/* Delete Guest Modal */}
      {showDeleteModal && selectedGuest && (
        <DeleteGuestModal
          guest={selectedGuest}
          clubId={clubId}
          currentUser={currentUser}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setSelectedGuest(null);
          }}
        />
      )}

      {/* Remove Email Confirmation Modal */}
      <ConfirmationModal
        isOpen={showRemoveEmailModal}
        onClose={handleCancelRemoveEmail}
        onConfirm={handleConfirmRemoveEmail}
        title="Remove Email Assignment"
        message={
          guestToRemoveEmail
            ? `Are you sure you want to remove the email from ${guestToRemoveEmail.first_name} ${guestToRemoveEmail.last_name || ''}? This will clear the email assignment and allow it to be reassigned to the correct guest account.`
            : ''
        }
        confirmText="Remove Email"
        cancelText="Cancel"
        isDangerous={false}
      />
    </div>
      )}
    </>
  );
};

export default GuestManagement;
